/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */
import { html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles/shared-styles.js';
import { timepickerStyles } from './sh-timepicker-styles.js';
import { TimeModifierMixin } from './time-modifier-mixin.js';
/**
 * @prop {String} amPm - AM or PM
 * @prop {String} h - hours
 * @prop {String} m - minutes
 * @prop {String} s - seconds
 * @prop {String} step - Increment when stepping up and down.
 * @prop {String} clockFormat - Either `12-hour` or `24-hour`.
 * @prop {Boolean} mini - Enables mini mode for picker which hides seconds
 * @prop {Boolean} disabled - Disables timepicker.
 * @prop {Boolean} timeInputState - *hide
 * @prop {Boolean} active - *hide
 */
export class SHTimepickerPanel extends TimeModifierMixin(LitElement) {
  static get properties() {
    return {
      h: { type: String, reflect: true },
      m: { type: String, reflect: true },
      s: { type: String, reflect: true },
      amPm: { type: String, reflect: true, attribute: 'am-pm' },
      mini: { type: Boolean, reflect: true },
      step: { type: String, reflect: true },
      clockFormat: { type: String, reflect: true, attribute: 'clock-format' },
      active: { type: Boolean, reflect: true },
    };
  }
  static get styles() {
    return [timepickerStyles, sharedStyles];
  }
  render() {
    return html`
      <div class="timepicker-panel-wrapper">
        <div class="timepicker-panel">
          <div class="timepicker-controls-wrapper">
            <div class="timepicker-controls hour-control">
              <sh-icon
                icon="up-s"
                id="dropdown-icon"
                data-id="h"
                button=""
                size="s"
                @click=${this._incrementWithButton}
              ></sh-icon>
              <div class="control-value">
                <input
                  type="text"
                  readonly
                  placeholder="hh"
                  data-id="h-ip"
                  id="h"
                  min="1"
                  max="${this.clockFormat === '12-hour' ? 12 : 23}"
                  .value="${this.h}"
                  @keydown=${this._onArrowKeyUpOrDownPressUpdateTime}
                  size="2"
                />
              </div>
              <sh-icon
                icon="down-s"
                id="dropdown-icon"
                data-id="h"
                button=""
                size="s"
                @click=${this._decrementWithButton}
              ></sh-icon>
            </div>
            <div
              class="seperator ${!(
                this.h === '' &&
                this.m === '' &&
                this.s === '' &&
                this.amPm === ''
              )
                ? 'colon-active'
                : ''}"
            >
              :
            </div>
            <div class="timepicker-controls min-control">
              <sh-icon
                icon="up-s"
                id="dropdown-icon"
                data-id="m"
                button=""
                size="s"
                @click=${this._incrementWithButton}
              ></sh-icon>
              <div class="control-value">
                <input
                  type="text"
                  readonly
                  placeholder="mm"
                  data-id="m-ip"
                  id="m"
                  min="0"
                  max="59"
                  size="2"
                  maxlength="2"
                  .value="${this.m}"
                  @keydown=${this._onArrowKeyUpOrDownPressUpdateTime}
                  size="2"
                />
              </div>
              <sh-icon
                icon="down-s"
                id="dropdown-icon"
                data-id="m"
                button=""
                size="s"
                @click=${this._decrementWithButton}
              ></sh-icon>
            </div>
            ${!this.mini
              ? html` <div
                    class="seperator ${!(
                      this.h === '' &&
                      this.m === '' &&
                      this.s === '' &&
                      this.amPm === ''
                    )
                      ? 'colon-active'
                      : ''} full"
                  >
                    :
                  </div>
                  <div class="timepicker-controls sec-control full">
                    <sh-icon
                      icon="up-s"
                      id="dropdown-icon"
                      data-id="s"
                      button=""
                      size="s"
                      @click=${this._incrementWithButton}
                    ></sh-icon>
                    <div class="control-value">
                      <input
                        type="text"
                        readonly
                        placeholder="ss"
                        data-id="s-ip"
                        class="full"
                        id="s"
                        min="0"
                        max="59"
                        maxlength="2"
                        .value="${this.s}"
                        @keydown=${this._onArrowKeyUpOrDownPressUpdateTime}
                        size="2"
                      />
                    </div>
                    <sh-icon
                      icon="down-s"
                      id="dropdown-icon"
                      data-id="s"
                      button=""
                      size="s"
                      @click=${this._decrementWithButton}
                    ></sh-icon>
                  </div>`
              : ''}
            <div
              class="seperator ${!(
                this.h === '' &&
                this.m === '' &&
                this.s === '' &&
                this.amPm === ''
              )
                ? 'colon-active'
                : ''}"
            ></div>
            ${this.clockFormat === '12-hour'
              ? html`
                    <div class="timepicker-controls ampm-control">
                      <sh-icon icon="up-s" id="dropdown-icon" button="" size="s" data-id="ap" @click=${this._incrementWithButton}"></sh-icon>
                      <div class="control-value"><input type="text" readonly placeholder="AM" readonly="true" data-id="ap-ip" id="amOrPm" .value="${this.amPm}" size="2" maxlength="2"
                             @keydown=${this._onArrowKeyUpOrDownPressUpdateTime}></div>
                      <sh-icon icon="down-s" id="dropdown-icon" button="" size="s" data-id="ap" @click=${this._decrementWithButton}></sh-icon>
                    </div>`
              : ''}
          </div>
        </div>
      </div>
    `;
  }
  constructor() {
    super();
    this.h = this.m = this.s = this.amPm = '';
    this.clockFormat = '12-hour';
    this.mini = false;
    this.step = 1;
  }
  _onArrowKeyUpOrDownPressUpdateTime(e) {
    this.incrementOrDecrementOnKeyDown(e);
  }
  _incrementWithButton(e) {
    e.stopPropagation();
    this.incrementTime(e.target);
  }
  _decrementWithButton(e) {
    e.stopPropagation();
    this.decrementTime(e.target);
  }
}

customElements.define('sh-timepicker-panel', SHTimepickerPanel);
